import React, { useEffect, useRef, useState } from 'react';
import { Box, Popper } from '@material-ui/core';
import { getReactComponent, getDescriptor, getDefaultProps } from '../registry/ComponentRegistry';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import styles from './CanvasRenderer.module.scss';
import { componentLabel } from '../registry/componentLabels';
import IntlMessages from 'util/IntlMessages';

/** Render the same component graph, with optional editor selection affordances. */
const CanvasNodeRenderer = ({ node, nodes, theme, mode, inspect, selectedNodeId, onNodeSelect, illustrative, dragEnabled, layoutContractVersion = 1, ancestors = [] }) => {
  const t = useLocalizedMessage();
  if (ancestors.includes(node.id)) return null;
  const ComponentClass = getReactComponent(node.type);
  const descriptor = getDescriptor(node.type);
  const props = { ...getDefaultProps(node.type), ...node.props };
  if (props.hidden || (descriptor?.modes && !descriptor.modes.includes(mode))) return null;

  const renderChild = (id) => {
    const child = nodes.find((candidate) => candidate.id === id);
    return child ? (
      <CanvasNodeRenderer
        key={child.id}
        node={child}
        nodes={nodes}
        theme={theme}
        mode={mode}
        inspect={inspect}
        selectedNodeId={selectedNodeId}
        onNodeSelect={onNodeSelect}
        illustrative={illustrative}
        dragEnabled={dragEnabled}
        layoutContractVersion={layoutContractVersion}
        ancestors={[...ancestors, node.id]}
      />
    ) : null;
  };
  const slots = Object.entries(node.slots || {});
  const children = slots.map(([slotName, ids]) => {
    const content = Array.isArray(ids) ? ids.map(renderChild) : null;
    // Each named column is one grid item, even when it contains several blocks.
    return node.type === 'columns'
      ? <div key={slotName} data-preview-slot={slotName} data-preview-slot-owner={dragEnabled ? node.id : undefined} className={`${styles.slot} ${dragEnabled && !ids?.length ? styles.emptyDropSlot : ''}`} style={{ gap: props.gap || 0 }}>{dragEnabled && !ids?.length && <span className={styles.emptySlotLabel}>{t('experience.design.dropHere')}</span>}{content}</div>
      : <React.Fragment key={slotName}>{content}</React.Fragment>;
  });
  const supportsIllustration = ['products', 'facets', 'sort', 'history', 'suggestions', 'catSuggestions', 'text', 'noResults', 'pagination', 'filtersButton'].includes(node.type);
  const rendered = ComponentClass ? (
    <ComponentClass
      {...props}
      {...(node.type === 'columns' ? { layoutContractVersion } : {})}
      {...(supportsIllustration ? { illustrative } : {})}
      theme={theme}
      data-preview-type={node.type}
    >
      {children.length > 0 ? children : undefined}
    </ComponentClass>
  ) : (
    <Box p={2} color="error.main">
      <IntlMessages id="ui.searchExperienceV2.core.renderer.canvasRenderer.unknownComponent" />
      {' '}{node.type}
    </Box>
  );
  // Invisible floating overlays remain available through the editor outline.
  if (!inspect || !onNodeSelect || ['floating', 'columns'].includes(node.type)) return rendered;
  const label = componentLabel(node.type, t);
  const selected = node.id === selectedNodeId;
  const select = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onNodeSelect(node);
  };
  return (
    <div
      className={`${styles.node} ${selected ? styles.selected : ''} ${(dragEnabled ? !slots.some(([, ids]) => ids?.length) : slots.length === 0) && descriptor?.category === 'container' ? styles.emptyContainer : ''}`}
      role="button"
      tabIndex={0}
      aria-label={t('searchInterface.preview.selectBlock', { block: label })}
      aria-pressed={selected}
      data-preview-node-id={node.id}
      draggable={dragEnabled && ancestors.length > 0}
      onDragStart={dragEnabled ? event => {
        event.stopPropagation();
        if (!ancestors.length) { event.preventDefault(); return; }
        event.dataTransfer.setData('existingNodeId', node.id);
        event.dataTransfer.effectAllowed = 'move';
      } : undefined}
      onClick={select}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) select(event);
      }}
    >
      {rendered}
    </div>
  );
};

const CanvasRenderer = ({ canvas, theme, currentScreen, currentDevice, mode = 'text', inspect = false, onNodeSelect, selectedNodeId, illustrative = false, dragEnabled = false }) => {
  const t = useLocalizedMessage();
  const rootRef = useRef(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  useEffect(() => {
    const elements = rootRef.current?.querySelectorAll('[data-preview-node-id]') || [];
    setSelectedElement(inspect ? [...elements].find(element => element.dataset.previewNodeId === selectedNodeId) || null : null);
  }, [canvas, selectedNodeId, inspect]);
  const anchor = inspect && (hoveredElement?.isConnected ? hoveredElement : selectedElement?.isConnected ? selectedElement : null);
  const anchorNode = anchor && canvas?.nodes?.find(node => node.id === anchor.dataset.previewNodeId);
  const boundary = rootRef.current?.closest('[data-preview-viewport]') || 'viewport';
  const trackHover = (event) => setHoveredElement(event.target.closest('[data-preview-node-id]'));
  if (canvas?.layoutContractVersion !== undefined && ![1, 2].includes(canvas.layoutContractVersion)) {
    return (
      <Box p={3} color="error.main" role="alert">
        {t('searchInterface.preview.unsupportedLayoutContract', {
          version: String(canvas.layoutContractVersion),
        })}
      </Box>
    );
  }
  if (!canvas?.nodes?.length) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4} color="text.secondary" textAlign="center">
        <Box mb={1}><IntlMessages id="ui.searchExperienceV2.core.renderer.canvasRenderer.noComponentsConfiguredForThisScreen" /></Box>
        <Box fontSize="0.75rem">
          <IntlMessages id="ui.searchExperienceV2.core.renderer.canvasRenderer.view" />
          {' '}{currentScreen}{' / '}{currentDevice}
        </Box>
      </Box>
    );
  }
  const rootNode = canvas.nodes.find((node) => node.id === canvas.root);
  if (!rootNode) {
    return <Box p={3} color="error.main"><IntlMessages id="ui.searchExperienceV2.core.renderer.canvasRenderer.rootNodeNotFound" /></Box>;
  }
  return (
    <Box ref={rootRef} display="flex" flexDirection="column" width="100%" height="100%"
      onMouseOver={inspect ? trackHover : undefined} onMouseLeave={() => setHoveredElement(null)}
      onFocusCapture={inspect ? trackHover : undefined} onBlurCapture={() => setHoveredElement(null)}>
      <CanvasNodeRenderer
        node={rootNode}
        nodes={canvas.nodes}
        layoutContractVersion={canvas.layoutContractVersion ?? 1}
        theme={theme}
        mode={mode}
        inspect={inspect}
        onNodeSelect={onNodeSelect}
        selectedNodeId={selectedNodeId}
        illustrative={illustrative}
        dragEnabled={dragEnabled}
      />
      {anchorNode && <Popper open anchorEl={anchor} placement="top-start" className={styles.labelPopper}
        modifiers={{
          offset: { offset: '0, 6' },
          preventOverflow: { boundariesElement: boundary, padding: 8 },
          flip: { boundariesElement: boundary, padding: 8 },
        }}>
        <span className={styles.label} aria-hidden="true">{componentLabel(anchorNode.type, t)}</span>
      </Popper>}
    </Box>
  );
};

export default CanvasRenderer;
